import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Calendar, User, ArrowRight, Mail, Twitter, Instagram, Linkedin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = "https://effendialogue-2.onrender.com";
const API = `${BACKEND_URL}/api`;


const HomePage = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [email, setEmail] = useState("");
  const [currentTagline, setCurrentTagline] = useState(0);

  const taglines = ["A Thought by Fadlan Raya Effendi"];

  useEffect(() => {
    fetchPosts();
    
    // Rotate taglines every 3 seconds
    const interval = setInterval(() => {
      setCurrentTagline(prev => (prev + 1) % taglines.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchPosts = async () => {
    try {
      // First, try to get featured post
      const featuredResponse = await axios.get(`${API}/posts?featured_only=true&limit=1`);
      const featuredPosts = featuredResponse.data;

      // Get all posts for the grid
      const allPostsResponse = await axios.get(`${API}/posts?limit=20`);
      const allPosts = allPostsResponse.data;

      if (featuredPosts.length > 0) {
        setFeaturedPost(featuredPosts[0]);
        // ✅ Include all posts (no filtering)
        setPosts(allPosts);
      } else if (allPosts.length > 0) {
        // Fallback to latest post if no featured post
        setFeaturedPost(allPosts[0]);
        setPosts(allPosts); // ✅ same here
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to fetch blog posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  document.title = "Effendialogue | Home";
}, []);


  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      fetchPosts();
      return;
    }

    try {
      const response = await axios.get(`${API}/posts/search?q=${encodeURIComponent(searchQuery)}`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error searching posts:", error);
      toast.error("Search failed");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatRelativeDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim()
      });
      
      if (response.data.status === 'already_subscribed') {
        toast.info("You're already subscribed to our newsletter!");
      } else if (response.data.status === 'resubscribed') {
        toast.success("Welcome back! You've been resubscribed.");
      } else {
        toast.success("Thanks for subscribing! Check your email (maybe spam) for confirmation.");
      }
      setEmail("");
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast.error("Failed to subscribe. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="app-container min-h-screen">
        <div className="content-wrapper py-20">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-700 rounded mb-8 w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-700 rounded-lg h-64"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen">
      {/* Hero Section */}
      <div className="hero-section-bg">
        <div className="hero-blur"></div>
        <div className="content-wrapper hero-section" style={{ paddingTop: '6rem' }}>
          <div className="text-center mb-16 fade-in">
            <h1 className="text-6xl font-bold main-heading gradient-text mb-4">
              Effendialogue
            </h1>
            <div className="rotating-tagline mb-6">
              {taglines[currentTagline]}
            </div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Where we trace the <em>soul</em> behind every idea — seeking not just <em>what</em> they are, but <em>why</em> they exist.
            </p>
                        {/* Search Bar */}
            <form onSubmit={handleSearch} className="search-container">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  data-testid="search-input"
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input pl-10 bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-500"
                />
                <Button 
                  data-testid="search-button"
                  type="submit" 
                  className="search-button bg-yellow-500 hover:bg-yellow-600 text-black px-6"
                >
                  Search
                </Button>
              </div>
            </form>
          </div>

          {/* Featured Hero Post */}
          {featuredPost && (
            <div className="mb-12 fade-up">
              <h2 className="text-3xl font-bold text-white mb-8">Featured Article</h2>
              <Card className="hero-card">
                <div className="flex flex-col md:flex-row gap-0">
                  {featuredPost.image_url && (
                    <div className="md:w-2/5 aspect-video md:aspect-[4/3] overflow-hidden">
                      <img
                        src={featuredPost.image_url}
                        alt={featuredPost.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 p-6">
                  <div className="featured-meta flex flex-wrap items-center gap-2 text-sm text-gray-400 mb-3 justify-start md:justify-start">
                    <Badge className="category-badge ml-0">
                      {featuredPost.category || 'general'}
                    </Badge>
                    <Calendar className="h-4 w-4" />
                    <span>{formatRelativeDate(featuredPost.created_at)}</span>
                    <User className="h-4 w-4 ml-2" />
                    <span>{featuredPost.author}</span>
                  </div>
                    <h3 className="text-2xl font-bold text-white mb-3 leading-tight">
                      {featuredPost.title}
                    </h3>
                    <p className="text-gray-300 mb-4 leading-relaxed">
                      {featuredPost.excerpt || featuredPost.content.substring(0, 150) + "..."}
                    </p>
                    <Link to={`/post/${featuredPost.id}`}>
                      <Button className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 font-semibold">
                        Read More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </div>
          )}

          <br></br>
          <br></br>
          
          {/* Main Blog Grid */}
          {posts.length > 0 && (
            <div className="fade-up">
              <h2 className="text-3xl font-bold text-white mb-8">Latest Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.slice(0, 6).map((post) => (
                  <Card
                    key={post.id}
                    data-testid={`post-card-${post.id}`}
                    className="post-card"
                  >
                    {post.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform hover:scale-105"
                        />
                      </div>
                    )}
                    <CardHeader className="pb-3">
                      <div className="post-meta flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4" />
                        <span>{formatRelativeDate(post.created_at)}</span>
                        <User className="h-4 w-4 ml-2" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <Badge className="category-badge text-xs">
                          {post.category || 'general'}
                        </Badge>
                      </div>
                      <h3 className="post-title text-xl font-semibold line-clamp-2 transition-colors">
                        {post.title}
                      </h3>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="post-excerpt line-clamp-3 mb-4">
                        {post.excerpt || post.content.substring(0, 150) + "..."}
                      </p>
                      <Link to={`/post/${post.id}`} data-testid={`read-more-${post.id}`}>
                        <Button className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 font-semibold">
                          Read More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* "More Articles" Button */}
              {posts.length > 6 && (
                <div className="text-center mt-10">
                  <Link to="/articles">
                      <Button className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 font-semibold">
                        More Articles
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
          <br></br>
          {/* Author Section */}
          <div id="author-section" className="category-section fade-up mb-12">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-6">About the Author</h2>
              <div className="max-w-4xl mx-auto">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  {/* Profile Image */}
                  <div className="w-48 h-48 rounded-full overflow-hidden shadow-2xl">
                    <img
                      src="https://i.imgur.com/gzRsOyS.jpeg"
                      alt="Fadlan Raya Effendi"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Author Text */}
                  <div className="flex-1 text-left">
                    <h3 className="text-2xl font-bold text-yellow-400 mb-4">Fadlan Raya Effendi</h3>
                    <p className="text-gray-300 text-lg leading-relaxed mb-4">
                      Hi, my name is <strong>Fadlan Raya Effendi</strong>, but you can call me <strong>Fadlan</strong>. 
                      I’m a computer science student who loves to write about anything that sparks my curiosity. 
                      Over the years, I’ve built countless projects, especially in the world of technology.
                    </p>
                    <p className="text-gray-300 text-lg leading-relaxed mb-6">
                      But one day, I realized something — I was too focused on <em>what</em> I was creating and not <em>why</em> I was creating it. 
                      That’s when <strong>Effendialogue</strong> was born.
                    </p>
                    <p className="text-gray-300 text-lg leading-relaxed mb-6">
                      <strong>Effendialogue</strong> is my space to reflect on the stories, emotions, and meanings behind every idea or project I bring to life. 
                      It’s where I explore the philosophy of creating — not just the outcome, but the thought process, the purpose, and the humanity behind it all.
                    </p>
                    <p className="text-gray-300 text-lg leading-relaxed mb-6">
                      Through these articles, I hope we can rediscover the <em>meaning</em> in what we do and inspire one another to think deeper. 
                      Even if it feels simple or old-fashioned, this mindset — questioning, reflecting, and understanding — is what turns us from mere <em>users</em> into true <em>thinkers</em>. <strong>Enjoy!</strong>
                    </p>

                    {/* Social Links */}
                    <div className="flex gap-4">
                      <a href="https://x.com/fadlaneffendii" target="_blank" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                        <Twitter className="w-6 h-6" />
                      </a>
                      <a href="https://linkedin.com/in/fadlan-raya-effendi-ba8585275" target="_blank" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                        <Linkedin className="w-6 h-6" />
                      </a>
                      <a href="https://instagram.com/fadlaneffendi_" target="_blank" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                        <Instagram className="w-6 h-6" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* Newsletter Signup */}
          <div className="newsletter-section fade-up">
            <h3 className="text-2xl font-bold text-white mb-4">Sign Up for Newsletter</h3>
            <p className="text-gray-300 mb-6">Join other readers to get the latest update about Effendialogue for FREE!</p>
            <form onSubmit={handleNewsletterSignup} className="flex gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="newsletter-input flex-1"
                required
              />
              <Button type="submit" className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 font-semibold">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-grid">
            <div>
              <h4 className="text-xl font-bold gradient-text mb-4">About Effendialogue</h4>
              <p className="text-gray-400 leading-relaxed">
                This is the place where I share you my thoughts that will hopefully make us appreciate every little thing behind every step that we been through.
              </p>
            </div>
            <div>
              <h5 className="text-lg font-semibold text-white mb-3">Quick Links</h5>
              <div className="footer-links">
                <Link to="/" className="footer-link">Home</Link>
                <Link to="/articles" className="footer-link">Articles</Link>
              </div>
            </div>
            <div>
              <h5 className="text-lg font-semibold text-white mb-3">Follow Me</h5>
              <div className="social-icons">
                <a href="https://x.com/fadlaneffendii" target="_blank" className="social-icon">
                  <Twitter className="w-6 h-6" />
                </a>
                <a href="https://instagram.com/fadlaneffendi_" target="_blank" className="social-icon">
                  <Instagram className="w-6 h-6" />
                </a>
                <a href="https://www.linkedin.com/in/fadlan-raya-effendi-ba8585275/" target="_blank" className="social-icon">
                  <Linkedin className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4 text-center">
            <p className="text-gray-500">© 2025 Effendialogue. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
